import React from 'react';
import {
  ChevronDown, ChevronUp, AlertTriangle, CheckCircle, Flame,
} from 'lucide-react';

/**
 * Section "Accise" (Élec ou TICGN Gaz) - Mode Pro uniquement.
 * Le rendu est conditionné par energyType.
 */
const AcciseSection = ({
  energyType,
  showAccise,
  setShowAccise,
  formatNumber,
  // Élec
  totalConsoMWh,
  puissanceSouscrite,
  nafCode,
  isNafEntre4et39,
  tauxAccise,
  tauxAcciseFutur,
  forceTauxPlein,
  setForceTauxPlein,
  acciseAutoActuel,
  acciseAutoFutur,
  // Gaz
  consoGazMWh,
  isNafIndustrielGaz,
  tauxAcciseGazActuel,
  tauxAcciseGazFutur,
  forceAcciseGazTauxPlein,
  setForceAcciseGazTauxPlein,
  ACCISE_GAZ_TAUX_STANDARD,
  ACCISE_GAZ_TAUX_REDUIT,
  acciseGazActuel,
  acciseGazFutur,
}) => {
  if (energyType === 'electricity') {
    // Libellé du sous-titre du taux futur (évite ternaire imbriqué)
    let tauxFuturSublabel = '(Même taux que budget actuel)';
    if (isNafEntre4et39) {
      tauxFuturSublabel = forceTauxPlein
        ? '(Taux plein forcé)'
        : '(Taux réduit NAF 04-39)';
    }
    return (
      <section className="card optional-section" data-testid="accise-section">
        <button
          className="section-toggle"
          onClick={() => setShowAccise(!showAccise)}
          data-testid="accise-toggle"
        >
          <h2 className="section-title">
            Accise sur l'Électricité <span className="annual-badge">Budget Annuel</span>
          </h2>
          {showAccise ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
        {showAccise && (
          <div className="optional-content">
            <div className="accise-info">
              <p className="accise-formula">
                <strong>Formule :</strong> Accise = Consommation (MWh) × Taux applicable (€/MWh)
              </p>
              <div className="accise-details">
                <div className="accise-detail-item">
                  <span className="detail-label">Consommation totale</span>
                  <span className="detail-value">{formatNumber(totalConsoMWh)} MWh</span>
                </div>
                <div className="accise-detail-item">
                  <span className="detail-label">Puissance souscrite</span>
                  <span className="detail-value">{puissanceSouscrite > 0 ? `${formatNumber(puissanceSouscrite)} kVA` : 'Non renseignée'}</span>
                </div>
                <div className="accise-detail-item">
                  <span className="detail-label">Code NAF</span>
                  <span className="detail-value">{nafCode || 'Non renseigné'}</span>
                  {nafCode && (
                    <span className="detail-sublabel">
                      {isNafEntre4et39 ? '(entre 04 et 39 → taux réduit)' : '(hors plage 04-39 → taux normal)'}
                    </span>
                  )}
                </div>
                <div className="accise-detail-item highlight">
                  <span className="detail-label">Taux Budget Actuel</span>
                  <span className="detail-value">{formatNumber(tauxAccise)} €/MWh</span>
                  <span className="detail-sublabel">
                    {puissanceSouscrite <= 36 ? '(3 à 36 kVA)' : '(37 kVA et plus)'}
                  </span>
                </div>
                <div className="accise-detail-item highlight">
                  <span className="detail-label">Taux Budget Futur</span>
                  <span className="detail-value">{formatNumber(tauxAcciseFutur)} €/MWh</span>
                  <span className="detail-sublabel">{tauxFuturSublabel}</span>
                </div>
                {/* Bouton pour forcer le taux plein (uniquement si NAF entre 4 et 39) */}
                {isNafEntre4et39 && (
                  <div className="accise-taux-toggle">
                    <button
                      className={`btn-taux-toggle ${forceTauxPlein ? 'taux-plein' : 'taux-reduit'}`}
                      onClick={() => setForceTauxPlein(!forceTauxPlein)}
                      data-testid="force-taux-plein-btn"
                    >
                      {forceTauxPlein ? (
                        <><CheckCircle size={16} /> Taux plein appliqué - Cliquer pour taux réduit</>
                      ) : (
                        <><AlertTriangle size={16} /> Taux réduit appliqué - Cliquer pour taux plein</>
                      )}
                    </button>
                    <p className="taux-toggle-help">
                      {forceTauxPlein
                        ? `Le budget futur utilise le taux plein (${formatNumber(tauxAccise)} €/MWh) au lieu du taux réduit.`
                        : `Votre code NAF (${nafCode}) permet un taux réduit de 0,50 €/MWh pour le budget futur.`}
                    </p>
                  </div>
                )}
              </div>
              <div className="accise-results">
                <div className="accise-result auto">
                  <span className="result-label">Accise Budget Actuel (calculé)</span>
                  <span className="result-value">{formatNumber(acciseAutoActuel)} €/an</span>
                </div>
                <div className="accise-result auto">
                  <span className="result-label">Accise Budget Futur (calculé)</span>
                  <span className="result-value">{formatNumber(acciseAutoFutur)} €/an</span>
                </div>
              </div>
              <p className="accise-disclaimer">
                Taux applicables du 1er février 2026 au 31 janvier 2027. TVA (20%) non incluse.
              </p>
            </div>
          </div>
        )}
      </section>
    );
  }

  // GAZ - TICGN
  // Sublabel du taux futur (évite ternaire imbriqué)
  let tauxFuturGazSublabel = '(Taux standard)';
  if (isNafIndustrielGaz) {
    tauxFuturGazSublabel = forceAcciseGazTauxPlein
      ? '(Taux plein forcé)'
      : '(Taux réduit industriel)';
  }
  return (
    <section className="card optional-section" data-testid="accise-gaz-section">
      <button
        className="section-toggle"
        onClick={() => setShowAccise(!showAccise)}
        data-testid="accise-gaz-toggle"
      >
        <h2 className="section-title">
          <Flame size={20} className="section-icon" style={{ color: '#F97316' }} />
          Accise sur le Gaz (TICGN) <span className="annual-badge">Budget Annuel</span>
        </h2>
        {showAccise ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>
      {showAccise && (
        <div className="optional-content">
          <div className="accise-info">
            <p className="accise-formula">
              <strong>Formule :</strong> Accise Gaz = Consommation (MWh) × Taux applicable (€/MWh)
            </p>
            <div className="accise-details">
              <div className="accise-detail-item">
                <span className="detail-label">Consommation gaz</span>
                <span className="detail-value">{formatNumber(consoGazMWh)} MWh</span>
              </div>
              <div className="accise-detail-item">
                <span className="detail-label">Code NAF</span>
                <span className="detail-value">{nafCode || 'Non renseigné'}</span>
                {nafCode && (
                  <span className="detail-sublabel">
                    {isNafIndustrielGaz ? '(Section B, C ou D → éligible taux réduit)' : '(Non industriel → taux standard)'}
                  </span>
                )}
              </div>
              <div className="accise-detail-item highlight">
                <span className="detail-label">Taux Budget Actuel</span>
                <span className="detail-value">{formatNumber(tauxAcciseGazActuel)} €/MWh</span>
                <span className="detail-sublabel">(Taux standard 2025)</span>
              </div>
              <div className="accise-detail-item highlight">
                <span className="detail-label">Taux Budget Futur</span>
                <span className="detail-value">{formatNumber(tauxAcciseGazFutur)} €/MWh</span>
                <span className="detail-sublabel">{tauxFuturGazSublabel}</span>
              </div>

              {/* Bouton pour forcer le taux plein (uniquement si NAF industriel) */}
              {isNafIndustrielGaz && (
                <div className="accise-taux-toggle">
                  <button
                    className={`btn-taux-toggle ${forceAcciseGazTauxPlein ? 'taux-plein' : 'taux-reduit'}`}
                    onClick={() => setForceAcciseGazTauxPlein(!forceAcciseGazTauxPlein)}
                    data-testid="force-taux-plein-gaz-btn"
                  >
                    {forceAcciseGazTauxPlein ? (
                      <><CheckCircle size={16} /> Taux plein appliqué - Cliquer pour taux réduit</>
                    ) : (
                      <><AlertTriangle size={16} /> Taux réduit appliqué - Cliquer pour taux plein</>
                    )}
                  </button>
                  <p className="taux-toggle-help">
                    {forceAcciseGazTauxPlein
                      ? `Le budget futur utilise le taux plein (${formatNumber(ACCISE_GAZ_TAUX_STANDARD)} €/MWh).`
                      : `Votre code NAF (${nafCode}) est industriel et permet un taux réduit de ${formatNumber(ACCISE_GAZ_TAUX_REDUIT)} €/MWh.`}
                  </p>
                </div>
              )}
            </div>
            <div className="accise-results">
              <div className="accise-result auto">
                <span className="result-label">Accise Gaz Budget Actuel</span>
                <span className="result-value">{formatNumber(acciseGazActuel)} €/an</span>
              </div>
              <div className="accise-result auto">
                <span className="result-label">Accise Gaz Budget Futur</span>
                <span className="result-value">{formatNumber(acciseGazFutur)} €/an</span>
              </div>
            </div>
            <p className="accise-disclaimer">
              Taux 2025 indicatif. TVA (20%) non incluse.
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default AcciseSection;
